/**
 * Offline Product Cache
 * Stores recently viewed products for offline access
 */

import { storage } from './storage';

const CACHE_KEY = 'offlineProductCache';
const MAX_CACHED_PRODUCTS = 50;

// In-memory cache for fast access
let memoryCache = new Map();
let cacheLoaded = false;

/**
 * Load cache from storage into memory
 */
export const loadProductCache = async () => {
  if (cacheLoaded) return;
  
  try {
    const cached = await storage.getItem(CACHE_KEY);
    if (cached && Array.isArray(cached)) {
      memoryCache = new Map(cached.map(p => [p.id || p.barcode, p]));
    }
    cacheLoaded = true;
  } catch (error) {
    console.error('Failed to load product cache:', error);
  }
};

/**
 * Save memory cache to storage
 */
const persistCache = async () => {
  try {
    const products = Array.from(memoryCache.values());
    await storage.setItem(CACHE_KEY, products);
  } catch (error) {
    console.error('Failed to persist product cache:', error);
  }
};

/**
 * Add or update a product in the cache
 * @param {Object} product - Product object with id/barcode
 */
export const cacheProduct = async (product) => {
  if (!product || (!product.id && !product.barcode)) return;
  
  const key = product.id || product.barcode;
  
  // Add timestamp for LRU eviction
  const cachedProduct = {
    ...product,
    _cachedAt: Date.now()
  };
  
  // If cache is full, remove oldest entry
  if (memoryCache.size >= MAX_CACHED_PRODUCTS && !memoryCache.has(key)) {
    let oldestKey = null;
    let oldestTime = Infinity;
    
    for (const [k, v] of memoryCache.entries()) {
      if (v._cachedAt < oldestTime) {
        oldestTime = v._cachedAt;
        oldestKey = k;
      }
    }
    
    if (oldestKey) {
      memoryCache.delete(oldestKey);
    }
  }
  
  memoryCache.set(key, cachedProduct);
  
  // Persist asynchronously (don't block)
  persistCache();
};

/**
 * Get a product from cache by barcode
 * @param {string} barcode 
 * @returns {Object|null}
 */
export const getCachedProductByBarcode = (barcode) => {
  if (!barcode) return null;
  
  // Direct lookup
  if (memoryCache.has(barcode)) {
    return memoryCache.get(barcode);
  }
  
  // Search by barcode field
  for (const product of memoryCache.values()) {
    if (product.barcode === barcode) {
      return product;
    }
  }
  
  return null;
};

/**
 * Search cached products by query
 * @param {string} query 
 * @returns {Array}
 */
export const searchCachedProducts = (query) => {
  if (!query) return [];
  
  const queryLower = query.toLowerCase().trim();
  const results = [];
  
  for (const product of memoryCache.values()) {
    const name = (product.name || '').toLowerCase();
    const brand = (product.brand || '').toLowerCase();
    const barcode = product.barcode || '';
    
    if (
      name.includes(queryLower) ||
      brand.includes(queryLower) ||
      barcode.includes(query)
    ) {
      results.push(product);
    }
  }
  
  // Sort by most recently cached
  return results.sort((a, b) => (b._cachedAt || 0) - (a._cachedAt || 0));
};

/**
 * Get all cached products
 * @returns {Array}
 */
export const getAllCachedProducts = () => {
  return Array.from(memoryCache.values())
    .sort((a, b) => (b._cachedAt || 0) - (a._cachedAt || 0));
};

/**
 * Get cache stats
 * @returns {Object}
 */
export const getCacheStats = () => ({
  count: memoryCache.size,
  maxSize: MAX_CACHED_PRODUCTS
});

/**
 * Clear the product cache
 */
export const clearProductCache = async () => {
  memoryCache.clear();
  await storage.removeItem(CACHE_KEY);
};
